import React, { useContext, useEffect, useRef, useState } from "react";
import styles from "./createJourney.module.css";
import ContainerHeader from "common/ContainerHeader";
import SaveButton from "components/SaveButton";
import FormFields from "components/Form";
import JourneyPackage from "components/Journey/Package";
import { postData, updateData, SearchOptions } from "utils/queries";
import { useNavigate } from "react-router-dom";
import { BurgerMenuContext } from "context/sidebar";

const CreateJourney = ({ journey_id, data }) => {
  const navigate = useNavigate();

  const [journeyDetails, setJourneyDetails] = useState({});
  const [tickets, setTickets] = useState(null);
  const [addOns, setAddOns] = useState(null);
  const [packages, setPackages] = useState(null);

  useEffect(() => {
    if (journey_id) {
      setJourneyDetails({
        journey_id: journey_id,
        event_name: data?.event_name ? { label: data.event_name, value: data.event_id } : null,
        journey_name: data?.journey_name,
        journey_image: data?.journey_image,
        description: data?.description, // Add description field
      });
    }
  }, [journey_id]);

  const handleChange = (label) => {
    return (e) => {
      if (label === "journey_image" || label === "event_name") {
        setJourneyDetails({ ...journeyDetails, [label]: e });
      } else {
        setJourneyDetails({ ...journeyDetails, [label]: e.target.value });
      }
    };
  };

  const fetchOptions = async (search = "") => {
    try {
      let optionsResult;
      
      if (!search || search.trim() === "") {
        // Load all upcoming events when no search term
        optionsResult = await SearchOptions(`event/events?eventType=upcoming&limit=100&offset=0&currentDate=${new Date().toISOString()}`);
        return optionsResult?.data?.data?.rows?.map((e) => ({
          value: e?.id,
          label: e?.event_name,
          starts: e?.starts,
          ends: e?.ends,
        })) || [];
      } else {
        // Use search endpoint when user types
        optionsResult = await SearchOptions(`ticket/search?key=${search}`);
        return optionsResult?.data?.data?.map((e) => ({
          value: e?.id,
          label: e?.event_name,
          starts: e?.starts,
          ends: e?.ends,
        })) || [];
      }
    } catch (error) {
      console.error("Error fetching options:", error);
      return [];
    }
  };

  const TicketAndPackage = true;
  const Fields = [
    {
      label: "Event Name",
      id: "event_name",
      placeholder: "Event Name",
      isDisabled: journey_id ? true : false,
      loadOptions: fetchOptions,
      defaultOptions: true,
      isSearchable: true,
      type: "select_search",
      value: journeyDetails?.event_name,
      required: true,
      loading: true,
      onChange: handleChange("event_name"),
    },
    {
      label: "Journey Name",
      id: "e.g: camping",
      placeholder: "Journey Name",
      type: "text",
      maxLength: 30,
      value: journeyDetails?.journey_name,
      required: true,
      onChange: handleChange("journey_name"),
    },
    {
      label: "Description",
      id: "journey_description",
      placeholder: "Enter journey description...",
      type: "text", // Assuming your FormFields component supports textarea
      maxLength: 50, // Adjust as needed
      value: journeyDetails?.description,
      required: false, // Set to true if description should be required
      onChange: handleChange("description"),
    },
    {
      parentLabel: "Journey Image",
      label: "Events with images are noticed by more customers, and usually sell more tickets.",
      id: "journey_image",
      type: "image",
      // disabled: journey_id ? true : false,
      value: journeyDetails?.journey_image,
      onChange: handleChange("journey_image"),
      required: true,
    },
  ];

  const firstButtonArgs = {
    label: "Cancel",
    id: "cancelTicket",
    type: "button",
    buttonOverrideInlineStyle: { marginRight: "1.6rem" },
    onClick: () => {navigate("/journey");},
  };

  const secondButtonArgs = {
    label: journey_id ? "Update" : "Create",
    id: "createTicket",
    type: "submit",
    onClick: () => {},
  };

  const handleSaveJourney = async (e) => {
    e?.preventDefault();

    const filteredPackages = packages
  ?.filter((packageItem) => packageItem.checked) 
  ?.map((packageItem) => ({
    package_id: packageItem.package_id,
    package_name: packageItem.name,
  }));

    // Filter tickets to include only checked variants
    const filteredTickets = tickets
      ?.map((ticket) => ({
        id: ticket.ticket_id,
        ticket_name: ticket.ticket_name,
        image_url: ticket.image_url,
        venue: ticket.venue,
        variants: ticket.variants
          ?.filter((variant) => variant.checked) 
          ?.map((variant) => ({
            variant_id: variant.variant_id,
            variant_name: variant.variant_name,
            checked: variant.checked,
          })),
        total_count: ticket.total_count,
      }))
      ?.filter((ticket) => ticket.variants.length > 0); 

    // Filter add_ons to include only checked variants
    const filteredAddOns = addOns
      ?.map((addOn) => ({
        id: addOn.ticket_id,
        ticket_name: addOn.ticket_name,
        image_url: addOn.image_url,
        venue: addOn.venue,
        variants: addOn.variants
          ?.filter((variant) => variant.checked) 
          ?.map((variant) => ({
            variant_id: variant.variant_id,
            variant_name: variant.variant_name,
            checked: variant.checked,
          })),
        total_count: addOn.total_count,
      }))
      ?.filter((addOn) => addOn.variants.length > 0); 

    if (journey_id) {
      // Update existing journey
      const result = await updateData(`journey/${journey_id}`, {
        ...journeyDetails,
        event_id: journeyDetails?.event_name?.value,
        packages: filteredPackages,
        tickets: filteredTickets, 
        add_ons: filteredAddOns,
      });
      if (result?.status === 200) {
        navigate("/journey");
      }
    } else {
      // Create new journey
      const result = await postData(`journey/create`, {
        ...journeyDetails,
        event_id: journeyDetails?.event_name?.value,
        packages: filteredPackages,
        tickets: filteredTickets, // Include filtered tickets in the payload
        add_ons: filteredAddOns,
      });
      // if (result?.status === 201) {
      //   navigate("/journey");
      // }
      if(result?.status===201){
        navigate("/journey")
      }else if (result?.status === 500) {
        window.scrollTo({ 
          top: document.documentElement.scrollHeight / 5, 
          behavior: "smooth" 
        });
      }
    }
  };

  const burgerContext = useContext(BurgerMenuContext);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        burgerContext.setShowSideBar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [burgerContext]);

  return (
    <>
      <ContainerHeader label="Create Ticket Journey" showAddButton={false} />
      <form onSubmit={handleSaveJourney} className={styles.container}>
        <div className={styles.add_journey}>
          <>
            {Fields?.map((e) => {
              if (e?.children) {
                return (
                  <>
                    {e?.parentLabel && (
                      <label
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        {e?.parentLabel}
                      </label>
                    )}
                    <div style={e?.style}>
                      <FormFields Fields={e?.children} />
                    </div>
                  </>
                );
              }
              return (
                <>
                  {e?.parentLabel && (
                    <label
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                      }}
                    >
                      {e?.parentLabel}
                    </label>
                  )}

                  <FormFields Fields={[e]} />
                </>
              );
            })}
          </>
        </div>
        <div
          className={styles.package}
          style={{ display: "flex", width: "100%" }}
          ref={sidebarRef}
        >
          <JourneyPackage
            setTickets={(tick) => {
              setTickets(tick);
            }}
            key={1}
            journey_id={journey_id}
            tickets={tickets}
            oldTickets={data?.tickets}
            type="all"
            event_id={journeyDetails?.event_name?.value}
            label="Step 1 : Include Tickets & Packages"
            description="Include Elements are the fundamentals of the package and not edit by customer."
            buttonLabel="Add Tickets"
            sidebarRef={sidebarRef}
          />
          <JourneyPackage
            setTickets={setPackages}
            key={2}
            journey_id={journey_id}
            tickets={packages}
            oldTickets={data?.packages}
            type="package"
            event_id={journeyDetails?.event_name?.value}
            buttonLabel="Add Packages"
            sidebarRef={sidebarRef}
            TicketAndPackage={TicketAndPackage}
          />
        </div>

        <div className={styles.addOns} ref={sidebarRef}>
          <JourneyPackage
            setTickets={setAddOns}
            key={3}
            journey_id={journey_id}
            tickets={addOns}
            oldTickets={data?.addOns}
            type="all"
            event_id={journeyDetails?.event_name?.value}
            label="Step 2: Include AddOns"
            description="Include Elements are the fundamentals of the package and not edit by customer."
            buttonLabel="Add AddOns"
            sidebarRef={sidebarRef}
          />
        </div>
        <SaveButton
          firstButtonArgs={firstButtonArgs}
          secondButtonArgs={secondButtonArgs}
        />
      </form>
    </>
  );
};

export default CreateJourney;